import { useEffect, useState } from "react";
import type jsPDF from "jspdf";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, FileText, Loader2 } from "lucide-react";

interface PdfPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pdfDoc: jsPDF | null;
  title?: string;
  fileName?: string;
}

/**
 * Reusable preview dialog for jsPDF documents.
 * Renders the PDF as a blob URL inside an iframe so the user can review
 * the final document before saving as draft or signing.
 */
export const PdfPreviewDialog = ({
  open,
  onOpenChange,
  pdfDoc,
  title = "Dokumentvorschau",
  fileName = "vorschau.pdf",
}: PdfPreviewDialogProps) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !pdfDoc) {
      return;
    }
    const blob = pdfDoc.output("blob");
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setBlobUrl(null);
    };
  }, [open, pdfDoc]);

  const handleDownload = () => {
    if (pdfDoc) pdfDoc.save(fileName);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[95vw] max-h-[95vh] h-[95vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-5 w-5 shrink-0" />
              <span className="truncate text-sm sm:text-base">{title}</span>
            </div>
            <Button variant="outline" size="sm" onClick={handleDownload} disabled={!pdfDoc}>
              <Download className="h-4 w-4 mr-2" />
              Herunterladen
            </Button>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden bg-muted/30 min-h-0">
          {blobUrl ? (
            <iframe
              src={blobUrl}
              className="w-full h-full border-0"
              title={title}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PdfPreviewDialog;
